import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
    static targets = ['wrapper', 'hero', 'content']

    declare readonly wrapperTarget: HTMLElement
    declare readonly hasWrapperTarget: boolean
    declare readonly heroTarget: HTMLElement
    declare readonly hasHeroTarget: boolean
    declare readonly contentTarget: HTMLElement
    declare readonly hasContentTarget: boolean

    private headerElement: HTMLElement | null = null

    private mediaQuery: MediaQueryList | null = null
    private isActive: boolean = false

    private timer: number | null = null
    private scrollDirection: 'up' | 'down' | null = null
    private lastScrollY: number = 0

    private resizeObserver: ResizeObserver | null = null

    wrapperTargetConnected(element: HTMLElement) {
        if (!this.hasContentTarget || !this.hasHeroTarget) {
            return
        }

        this.mediaQuery = matchMedia('(min-width: 768px)')
        this.mediaQuery.addEventListener('change', this.handleMediaQueryChange)

        this.resizeObserver = new ResizeObserver(this.handleContentResize)
        this.resizeObserver.observe(this.contentTarget)

        this.isActive = !this.mediaQuery?.matches
        this.lastScrollY = window.scrollY
        this.headerElement = document.querySelector('.header')

        this.scrollIfNeeded()

        window.addEventListener('scroll', this.handleScroll)
    }

    wrapperTargetDisconnected(element: HTMLElement) {
        if (!this.hasContentTarget || !this.hasHeroTarget) {
            return
        }

        this.mediaQuery?.removeEventListener('change', this.handleMediaQueryChange)
        window.removeEventListener('scroll', this.handleScroll)
    }

    handleContentResize = (entries: ResizeObserverEntry[]) => {
        entries.forEach((entry) => {
            if (entry.target === this.contentTarget) {
                const newHeight = entry.contentRect.height
                const footerHeight = document.querySelector('.footer')?.getBoundingClientRect().height ?? 0

                this.isActive = !this.mediaQuery?.matches

                if ((newHeight + footerHeight) < window.innerHeight) {
                    this.isActive = false
                    this.wrapperTarget.classList.add('disabled')
                } else {
                    this.wrapperTarget.classList.remove('disabled')
                }

                this.toggleActive()
                this.scrollIfNeeded()
            }
        })
    }

    handleMediaQueryChange = () => {
        this.isActive = !this.mediaQuery?.matches

        if (this.isActive) {
            this.scrollIfNeeded()
        }
    }

    handleScroll = (event: Event) => {
        if (!this.isActive) {
            return
        }

        this.checkScrollDirection()
        this.toggleActive()

        if (this.timer) {
            clearTimeout(this.timer)
        }

        this.timer = setTimeout(() => {
            this.scrollIfNeeded()
        }, 50)
    }

    private scrollIfNeeded() {
        if (this.isHeroInViewport()) {
            if (this.scrollDirection === 'down' && window.scrollY > 0) {
                this.scrollToContent()
                this.wrapperTarget.classList.add('scrolled')
            } else {
                this.scrollToTop()
                this.wrapperTarget.classList.remove('scrolled')
            }
        } else {
            this.wrapperTarget.classList.add('scrolled')
        }

        this.toggleActive()
    }

    private scrollToContent() {
        const headerHeight = Math.floor(this.headerElement?.getBoundingClientRect().height ?? 0)
        const contentY = this.contentTarget.offsetTop - headerHeight

        window.scrollTo({ top: contentY, behavior: 'smooth' })
    }

    private scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    private checkScrollDirection() {
        const currentScrollY = window.scrollY

        if (currentScrollY === 0) {
            this.scrollDirection = 'up'
        } else if (currentScrollY > this.lastScrollY) {
            this.scrollDirection = 'down'
        } else {
            this.scrollDirection = 'up'
        }

        this.lastScrollY = currentScrollY
    }

    private isHeroInViewport() {
        const threshold = Math.floor(this.headerElement?.getBoundingClientRect().height ?? 0)
        const contentTop = Math.floor(this.contentTarget.getBoundingClientRect().top)

        return contentTop > threshold
    }

    private toggleActive() {
        if (!this.isActive) {
            return this.wrapperTarget.classList.remove('active')
        }

        const heroBottom = Math.floor(this.heroTarget.getBoundingClientRect().bottom)
        const contentTop = Math.floor(this.contentTarget.getBoundingClientRect().top)

        if (heroBottom > contentTop) {
            this.wrapperTarget.classList.remove('active')
        } else {
            this.wrapperTarget.classList.add('active')
        }
    }
}
